import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Hospital, Me, MeTenant } from '../types/api';
import { userService } from '../services/userService';
import { performLogout } from '../utils/auth';
import { ensureTenant } from '../services/auth/brokerSession';
import { updateUserData, setCurrentHospitalRoles } from './userSlice';

const toHospital = (tenant: MeTenant): Hospital => ({
  tenantId: tenant.tenantId,
  hospitalName: tenant.displayName,
  hospitalCode: tenant.ehrPracticeId,
  ehr: tenant.ehr,
});

interface HospitalState {
  hospitals: Hospital[];
  selectedHospital: Hospital | null;
  loading: boolean;
  error: string | null;
  // True from the moment a tenant is picked until the broker token has been
  // switched to it (and the subsequent verify completes). Gates the dropdown so
  // a tenant-scoped fetch can't fire mid-switch against the previous token.
  switchingTenant: boolean;
  userVerificationLoading: boolean;
  userVerificationError: string | null;
  lastVerifiedTenantId: string | null; // Track last verified tenant to prevent duplicate calls
}

const initialState: HospitalState = {
  hospitals: [],
  selectedHospital: null,
  loading: false,
  error: null,
  switchingTenant: false,
  userVerificationLoading: false,
  userVerificationError: null,
  lastVerifiedTenantId: null,
};

// Async thunk for fetching hospitals
export const fetchHospitals = createAsyncThunk(
  'hospitals/fetchHospitals',
  async (_, { dispatch, rejectWithValue, getState }) => {
    try {
      const state = getState() as { hospitals: HospitalState };
      // Return existing data unless we have none or a prior error
      if (state.hospitals.hospitals.length > 0 && !state.hospitals.error) {
        return state.hospitals.hospitals;
      }
      const me = await userService.getMe();
      dispatch(updateUserData(me));
      return me.tenants.map(toHospital);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch hospitals';
      return rejectWithValue(errorMessage);
    }
  },
);

// Async thunk for user verification
export const verifyUserAccess = createAsyncThunk(
  'hospitals/verifyUserAccess',
  async (tenantId: string, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState() as {
        hospitals: HospitalState;
        user: { user: Me | null };
      };
      // Prevent duplicate verification calls for the same tenant
      if (
        state.hospitals.lastVerifiedTenantId === tenantId &&
        !state.hospitals.userVerificationError
      ) {
        return { success: true, message: 'Already verified' };
      }

      // Obtain a Maia token scoped to this tenant from the broker (cached per
      // tenant, re-minted only when needed). Receiving a tenant-scoped token IS
      // the access check: the broker only mints one for a tenant the user
      // belongs to, so a failure here means no access. After this the active
      // token's tenant is `tenantId`, so every later backend call carries the
      // correctly-scoped token with no Auth0 round-trip.
      await ensureTenant(tenantId);

      // Roles/permissions come from the /v1/me identity read held in state
      const me = state.user.user;
      const selectedHospital = state.hospitals.selectedHospital;

      if (me && selectedHospital) {
        const tenant = me.tenants.find(
          (t) => t.tenantId === selectedHospital.tenantId,
        );

        if (tenant) {
          dispatch(
            setCurrentHospitalRoles({
              roles: tenant.roles,
              permissions: tenant.permissions,
            }),
          );
        }
      }

      return { success: true as const, message: 'Access verified' };
    } catch (error: unknown) {
      // ensureTenant throwing means the broker would not mint a token for this
      // tenant — i.e. no access. Surface it as a verification failure.
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to verify user access';
      return rejectWithValue(errorMessage);
    }
  },
);

const hospitalSlice = createSlice({
  name: 'hospitals',
  initialState,
  reducers: {
    setSelectedHospital: (state, action: PayloadAction<Hospital | null>) => {
      // Only update if the hospital actually changed
      if (state.selectedHospital?.tenantId !== action.payload?.tenantId) {
        state.selectedHospital = action.payload;
        // Reset verification state when hospital changes
        state.userVerificationError = null;
        state.lastVerifiedTenantId = null;
      }
    },
    clearHospitals: (state) => {
      state.hospitals = [];
      state.selectedHospital = null;
      state.error = null;
      state.switchingTenant = false;
      state.userVerificationError = null;
      state.lastVerifiedTenantId = null;
    },
    clearUserVerificationError: (state) => {
      state.userVerificationError = null;
    },
    setTenantSwitching: (state, action: PayloadAction<boolean>) => {
      state.switchingTenant = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchHospitals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHospitals.fulfilled, (state, action) => {
        state.loading = false;
        // Only update if the data actually changed
        if (
          JSON.stringify(state.hospitals) !== JSON.stringify(action.payload)
        ) {
          state.hospitals = action.payload;
          // Auto-selection of the first tenant is handled in the useHospitals
          // hook via selectHospital, so the broker token is switched to it
          // before the selection is published (and any tenant-scoped fetch runs).
        }
      })
      .addCase(fetchHospitals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(verifyUserAccess.pending, (state) => {
        state.userVerificationLoading = true;
        state.userVerificationError = null;
      })
      .addCase(verifyUserAccess.fulfilled, (state, action) => {
        state.userVerificationLoading = false;
        state.switchingTenant = false;
        if (action.payload.success) {
          // Track the successfully verified tenant
          state.lastVerifiedTenantId = state.selectedHospital?.tenantId || null;
        } else {
          // If verification fails, show error message briefly then logout
          state.userVerificationError =
            action.payload.message || 'Access denied for this hospital';
          setTimeout(() => {
            performLogout();
          }, 1000);
        }
      })
      .addCase(verifyUserAccess.rejected, (state, action) => {
        state.userVerificationLoading = false;
        state.switchingTenant = false;
        state.userVerificationError = action.payload as string;
        setTimeout(() => {
          performLogout();
        }, 2000);
      });
  },
});

export const {
  setSelectedHospital,
  clearHospitals,
  clearUserVerificationError,
  setTenantSwitching,
} = hospitalSlice.actions;
export default hospitalSlice.reducer;
